import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Divider,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormControl,
  FormLabel,
  TextField,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import palette from 'src/theme/palette';
import { toast } from 'react-toastify';
import { respondInquiry } from 'src/ApiServices';
import useMediaQuery from '@mui/material/useMediaQuery';

const InquirySettingsModal = ({ open, onClose, inquiry, onStatusChange }) => {
  const [approved, setApproved] = useState(inquiry?.status === 'approved');
  const [rejected, setRejected] = useState(inquiry?.status === 'rejected');
  const [customMessage, setCustomMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');

  // Pre-defined messages
  const statusMessages = {
    approved:
      "Thank you for your inquiry! We'll get back to you shortly with more information about the property.",
    rejected:
      "Thank you for your interest. Unfortunately, we're unable to assist with your inquiry at this time."
  };

  const handleApprovedChange = (event) => {
    const isChecked = event.target.checked;
    setApproved(isChecked);

    if (isChecked) {
      setRejected(false);
      setCustomMessage(statusMessages.approved);
    } else {
      setCustomMessage('');
    }
  };

  const handleRejectedChange = (event) => {
    const isChecked = event.target.checked;
    setRejected(isChecked);

    if (isChecked) {
      setApproved(false);
      setCustomMessage(statusMessages.rejected);
    } else {
      setCustomMessage('');
    }
  };

  const getCurrentStatusId = () => {
    if (approved) return 13; // Approved
    if (rejected) return 14; // Rejected
    return null;
  };

  const handleSubmit = async () => {
    if (!inquiry) return;

    const statusId = getCurrentStatusId();
    if (!statusId) {
      toast.error('Please select either Approved or Rejected');
      return;
    }

    if (!customMessage.trim()) {
      toast.error('Please enter a response message');
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        status: statusId,
        message: customMessage,
        id: inquiry.id
      };

      await respondInquiry(payload);
      if (onStatusChange) {
        await onStatusChange(inquiry.id, statusId === 13 ? 'approved' : 'rejected', customMessage);
      }

      toast.success(`Inquiry status updated successfully`);
      onClose();
    } catch (error) {
      console.error('Failed to update inquiry status:', error);
      toast.error('Failed to update inquiry status');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircleIcon />;
      case 'rejected':
        return <CancelIcon />;
      default:
        return <SettingsIcon />;
    }
  };

  if (!inquiry) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          margin: isMobile ? 0 : undefined,
          minHeight: '400px',
          maxHeight: isMobile ? '100vh' : '90vh',
          width: isMobile ? '100vw' : '1400px'
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${palette.primary.main} 0%, ${palette.primary.dark} 100%)`,
          color: palette.primary.contrastText,
          py: 2,
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon />
          <Typography
            sx={{
              fontSize: 18,
              fontFamily: 'poppinsSemiBold'
            }}
          >
            Inquiry Settings
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: palette.primary.contrastText,
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Inquiry Info */}
          <Grid item xs={12}>
            <Box
              sx={{
                backgroundColor: palette.grey[50],
                p: 2,
                borderRadius: 1,
                mt: 2,
                border: `1px solid ${palette.grey[200]}`
              }}
            >
              <Typography variant="subtitle2" sx={{ fontFamily: 'poppinsMedium', mb: 1 }}>
                Inquiry: {inquiry.name}
              </Typography>
              <Typography variant="body2" sx={{ color: palette.text.secondary }}>
                {inquiry.email} • {inquiry.contact_no}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  icon={getStatusIcon(inquiry.status || 'pending')}
                  label={inquiry.status ? inquiry.status.toUpperCase() : 'PENDING'}
                  color={getStatusColor(inquiry.status || 'pending')}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Status Selection  */}
          <Grid item xs={12}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel
                component="legend"
                sx={{
                  fontFamily: 'poppinsSemiBold',
                  color: palette.text.primary,
                  mb: 3,
                  fontSize: '16px'
                }}
              >
                Approve and Reject Inquiry
              </FormLabel>
              <FormGroup
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 4,
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {/* Approved Checkbox*/}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={approved}
                      onChange={handleApprovedChange}
                      icon={<CheckCircleIcon sx={{ fontSize: 32, color: palette.grey[400] }} />}
                      checkedIcon={
                        <CheckCircleIcon sx={{ fontSize: 32, color: palette.success.main }} />
                      }
                      sx={{
                        padding: '8px',
                        '&:hover': {
                          backgroundColor: 'transparent'
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography
                        sx={{
                          fontFamily: 'poppinsSemiBold',
                          fontSize: '16px',
                          color: approved ? palette.success.main : palette.text.primary
                        }}
                      >
                        Approve
                      </Typography>
                    </Box>
                  }
                  sx={{
                    margin: 0,
                    flexDirection: 'column',
                    alignItems: 'center',
                    '& .MuiFormControlLabel-label': {
                      marginLeft: 0,
                      marginTop: 1
                    }
                  }}
                />

                {/* Rejected Checkbox */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rejected}
                      onChange={handleRejectedChange}
                      icon={<CancelIcon sx={{ fontSize: 32, color: palette.grey[400] }} />}
                      checkedIcon={<CancelIcon sx={{ fontSize: 32, color: palette.error.main }} />}
                      sx={{
                        padding: '8px',
                        '&:hover': {
                          backgroundColor: 'transparent'
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography
                        sx={{
                          fontFamily: 'poppinsSemiBold',
                          fontSize: '16px',
                          color: rejected ? palette.error.main : palette.text.primary
                        }}
                      >
                        Reject
                      </Typography>
                    </Box>
                  }
                  sx={{
                    margin: 0,
                    flexDirection: 'column',
                    alignItems: 'center',
                    '& .MuiFormControlLabel-label': {
                      marginLeft: 0,
                      marginTop: 1
                    }
                  }}
                />
              </FormGroup>
            </FormControl>
          </Grid>

          {/* Custom Message */}
          <Grid item xs={12}>
            <TextField
              label="Response Message"
              multiline
              rows={4}
              fullWidth
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Select Approved or Rejected to auto-fill message, or enter your custom message here..."
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: palette.primary.main },
                  '&.Mui-focused fieldset': { borderColor: palette.primary.main }
                }
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          gap: 1,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'center', sm: 'stretch' },
          justifyContent: { xs: 'center', sm: 'flex-end' }
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: palette.grey[400],
            color: palette.text.primary,
            '&:hover': {
              borderColor: palette.grey[600]
            },
            minWidth: '100px'
          }}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={<CheckCircleIcon />}
          sx={{
            background: `linear-gradient(135deg, ${palette.success.main} 0%, ${palette.success.dark} 100%)`,
            color: palette.success.contrastText,
            '&:hover': {
              background: `linear-gradient(135deg, ${palette.success.dark} 0%, ${palette.success.dark} 100%)`
            },
            minWidth: '140px'
          }}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Updating...' : 'Send Response'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InquirySettingsModal;
